<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-green" sprite="phone-number" />
          Phone Books
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-green" sprite="phone-number" />
            Phone Books
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable />
        </div>
        <div class="box right">
          <el-button @click="showModal('Create')">Add New</el-button>
        </div>
      </div>
      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <phonebooks-form
      :visible="modalVisible"
      :context="modalContext"
      :modalProcessing="modalProcessing"
      :selectedRecord="selectedRecord"
      :clearForm="clearFields"
      @createOperation="createOperation"
      @editOperation="editOperation"
      @modalClosed="onModalClosed"
    />
  </div>
</template>

<script>
import feathersClient from "../../../feathers/feathers-client";
import { debounce } from "lodash";
import { globalEventBus } from "../../../globalEventBus";
import PhonebooksForm from "./components/form";

export default {
  name: "PhonebooksTable",
  data() {
    return {
      phonebbookChannel: null,
      tableLoading: false,
      tableData: [],
      allPhonebooks: [],
      search: "",
      visibleActionsId: "",
      total: 0,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-green" sprite="phone-number" />
                </div>,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          sortable: true,
          minWidth: 150,
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.showModal("Edit", row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
      modalVisible: false,
      modalContext: "",
      modalProcessing: false,
      selectedRecord: null,
      clearFields: false,
    };
  },

  components: {
    PhonebooksForm,
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
  },

  watch: {
    selectedTenant() {
      this.findPhonebooks();
      feathersClient
        .service("channel-memberships")
        .remove(null, { query: { channelName: this.phonebookChannel } });
      this.joinSocketChannel();
    },

    user() {
      this.findPhonebooks();
      if (!this.phonebookChannel) {
        this.joinSocketChannel();
      }
    },

    search: debounce(function () {
      this.searchPhonebooks();
    }, 500),
  },

  methods: {
    searchPhonebooks() {
      if (this.search.length) {
        this.tableData = this.tableData.filter((data) =>
          data.name.toLowerCase().includes(this.search.toLowerCase())
        );
      } else {
        this.tableData = [...this.allPhonebooks];
      }
      this.total = this.tableData.length;
    },

    showModal(context, editId = null) {
      this.modalContext = context;
      this.modalVisible = true;
      this.clearFields = false;

      this.selectedRecord = null;
      if (editId) {
        this.modalProcessing = true;
        feathersClient
          .service("phonebooks")
          .get(editId)
          .then((res) => {
            this.selectedRecord = res;
            this.modalProcessing = false;
          });
      }
    },

    onModalClosed() {
      this.modalVisible = false;
      this.modalContext = "";
      this.clearFields = true;
    },

    // ⭐️ General Form and Table Operations ⭐️ //
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.showModal("Edit", data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    // ⭐️ CRUD Operations ⭐️ //
    isValidName(name) {
      const regex = /^OMNIA-[1-9][\d]*$/;

      if (regex.test(name) || name.toLowerCase() === "default") {
        this.$notify({
          title: "Invalid name format!",
          message: "Please use a regular name.",
          type: "error",
        });
        return false;
      }

      return true;
    },

    sendRequest({ confirm, success, type }, ...args) {
      const allowedRequests = ["create", "patch", "remove"];
      if (!allowedRequests.includes(type)) {
        return;
      }

      this.$confirm(confirm, "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.tableLoading = true;
          this.modalProcessing = true;

          feathersClient
            .service("phonebooks")
            // eslint-disable-next-line no-unexpected-multiline
            [type](...args)
            .then(() => {
              this.tableLoading = false;
              this.modalProcessing = false;
              this.modalVisible = false;
              this.clearFields = true;

              this.$notify({
                title: "Success",
                message: success,
                type: "success",
              });
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }

              this.tableLoading = false;
              this.modalProcessing = false;
            });
        })
        .catch(() => true);
    },

    createOperation(createBody) {
      if (this.isValidName(createBody.name)) {
        this.sendRequest(
          {
            confirm: "Process phonebook create?",
            success: "Created successfully!",
            type: "create",
          },
          createBody
        );
      }
    },

    editOperation(editBody) {
      if (this.isValidName(editBody.name)) {
        this.sendRequest(
          {
            confirm: "Process phonebook edit?",
            success: "Edited successfully!",
            type: "patch",
          },
          this.selectedRecord.id,
          editBody
        );
      }
    },

    deleteOperation(id) {
      this.sendRequest(
        {
          confirm: "Process phonebook delete?",
          success: "Deleted successfully!",
          type: "remove",
        },
        id
      );
    },

    findPhonebooks() {
      this.tableLoading = true;
      this.tableData = [];
      feathersClient
        .service("phonebooks")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            provisioning: 1,
            $sort: {
              name: 1,
            },
          },
        })
        .then((res) => {
          this.tableData = res.filter(
            ({ name, includeExt }) =>
              !(name === "Default")
          );
          this.allPhonebooks = [...this.tableData];
          this.total = this.tableData.length;
          this.tableLoading = false;
        });
    },

    // ⭐️ REAL TIME Feedback ⭐️ //
    joinSocketChannel() {
      const channelService = feathersClient.service("channel-memberships");
      const data = {
        roomType: "admins",
        tenantId: this.selectedTenant.id,
      };
      channelService.create(data).then(() => {
        this.phonebookChannel = `${data.roomType}/${data.tenantId}`;
      });
    },

    removeListeners() {
      feathersClient.service("phonebooks").removeListener("created");
      feathersClient.service("phonebooks").removeListener("patched");
      feathersClient.service("phonebooks").removeListener("removed");
      globalEventBus.$off("appReconnected");
    },
  },

  created() {
    // ⭐️ EVENT LISTENERS ⭐️ //

    // ⭐️ monitor socket connection and reconnect to channel ⭐️ //
    globalEventBus.$on("appReconnected", () => {
      this.joinSocketChannel();
    });

    // ⭐️ record was created ⭐️ //
    feathersClient.service("phonebooks").on("created", (data) => {
      this.tableData.push(data);
      this.allPhonebooks.push(data);
    });

    // ⭐️ record was edited ⭐️ //
    feathersClient.service("phonebooks").on("patched", (data) => {
      const updateIndex = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const updateAllIndex = this.allPhonebooks.findIndex(
        (item) => item.id === data.id
      );

      if (updateIndex >= 0) {
        this.tableData.splice(updateIndex, 1, data);
      }
      if (updateAllIndex >= 0) {
        this.allPhonebooks.splice(updateAllIndex, 1, data);
      }
    });

    // ⭐️ record was deleted ⭐️ //
    feathersClient.service("phonebooks").on("removed", (data) => {
      const indexToRemove = this.tableData.findIndex(
        (item) => item.id === data.id
      );
      const indexAllToRemove = this.allPhonebooks.findIndex(
        (item) => item.id === data.id
      );
      if (indexToRemove >= 0) {
        this.tableData.splice(indexToRemove, 1);
      }
      if (indexAllToRemove >= 0) {
        this.allPhonebooks.splice(indexAllToRemove, 1);
      }
    });
  },
  mounted() {
    if (this.user) {
      this.joinSocketChannel();
      this.findPhonebooks();
    }
  },

  beforeDestroy() {
    feathersClient
      .service("channel-memberships")
      .remove(null, { query: { channelName: this.phonebookChannel } });
    this.removeListeners();
  },
};
</script>
